import React, { useEffect, useState } from "react";
import useApiFetcher from "../../hooks/useApiFetcher";
import Modal from "../Modal/Modal";
import { FaEdit, FaRegPlusSquare } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import LoaderSpiner from "../LoaderSpiner";
import { useTheme } from "../BrandColors/Context/ThemeContext";

const inputFields = [
    { label: "Car Name", name: "carName", type: "text" },
    { label: "Model", name: "model", type: "text" },
    { label: "Year", name: "year", type: "number" },
    { label: "Color Name", name: "colorDetails.name", type: "text" },
    { label: "Color Mode", name: "colorDetails.mode", type: "text" },
];

const Cars = () => {
    const dealershipTheme = useTheme() || "#3b82f6";
    const { apiFetcher } = useApiFetcher();
    const user = JSON.parse(localStorage.getItem("user"));
    const [cars, setCars] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [editMode, setEditMode] = useState(null);
    const isEdit = editMode && Object?.keys(editMode)?.length;

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const [formData, setFormData] = useState({
        carName: "",
        model: "",
        year: "",
        colorDetails: {
            name: "",
            mode: "",
        },
    });

    const fetchCars = async () => {
        try {
            setIsLoading(true);
            const response = await apiFetcher(`/cars?dealershipId=${user?.dealershipId?._id}`);
            setCars(response);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching cars:", error);
            setIsLoading(false);
        }
    };

    const openModal = (car = null) => {
        setEditMode(car);
        if (car) {
            setFormData({
                carName: car.carName || "",
                model: car.model || "",
                year: car.year || "",
                colorDetails: {
                    name: car.colorDetails?.name || "",
                    mode: car.colorDetails?.mode || "",
                },
            });
        } else {
            setFormData({
                carName: "",
                model: "",
                year: "",
                colorDetails: {
                    name: "",
                    mode: "",
                },
            });
        }
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setEditMode(null);
        setFormData({
            carName: "",
            model: "",
            year: "",
            colorDetails: {
                name: "",
                mode: "",
            },
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name.includes(".")) {
            const [mainKey, subKey] = name.split(".");
            setFormData((prev) => ({
                ...prev,
                [mainKey]: {
                    ...prev[mainKey],
                    [subKey]: value,
                },
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEdit) {
                await apiFetcher(`/cars/${editMode._id}`, "PUT", formData);
            } else {
                await apiFetcher("/cars", "POST", { ...formData, dealershipId: user?.dealershipId?._id });
            }
            fetchCars();
            closeModal();
        } catch (error) {
            console.error("Error submitting car form:", error);
        }
    };

    const handleDelete = async (id) => {
        try {
            await apiFetcher(`/cars/${id}`, "DELETE");
            fetchCars();
        } catch (error) {
            console.error("Error deleting car:", error);
        }
    };

    useEffect(() => {
        fetchCars();
    }, []);

    return (
        <div className="glass-panel w-full flex flex-col p-6 animate-fade-in-up mt-8 min-h-[60vh] ">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <h2 className="text-2xl font-bold">Car Inventory</h2>

                <button
                    onClick={() => openModal()}
                    className="w-full md:w-auto btn-primary whitespace-nowrap shadow-lg flex items-center gap-2 justify-center"
                    style={{ backgroundColor: dealershipTheme }}
                >
                    Create Car <FaRegPlusSquare size={16} />
                </button>
            </div>

            {isLoading ? (
                <div className="flex justify-center items-center py-20 flex-1">
                    <LoaderSpiner />
                </div>
            ) : cars.length === 0 ? (
                <div className="flex justify-center items-center py-16 bg-white/30 rounded-lg border border-blue-200/50">
                    <p className="text-slate-600">No cars found in inventory.</p>
                </div>
            ) : (
                <div className="w-full overflow-x-auto rounded-lg border border-blue-200/50 scrollbar-hide">
                    <table className="w-full text-left border-collapse min-w-max">
                        <thead>
                            <tr className="text-white text-sm font-semibold whitespace-nowrap" style={{ backgroundColor: dealershipTheme }}>
                                <th className="px-4 py-4 rounded-tl-lg">Car Name</th>
                                <th className="px-4 py-4">Model</th>
                                <th className="px-4 py-4">Year</th>
                                <th className="px-4 py-4 rounded-tr-lg">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50 bg-white/30">
                            {cars?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((car) => (
                                <tr key={car._id} className="text-slate-700 text-sm hover:bg-white/5 transition-colors group">
                                    <td className="px-4 py-4 font-medium">{car.carName}</td>
                                    <td className="px-4 py-4">{car.model}</td>
                                    <td className="px-4 py-4">{car.year}</td>
                                    <td className="px-4 py-4">
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => openModal(car)}
                                                className="text-primary-400 hover:text-primary-300 hover:scale-110 transition-transform p-1.5 rounded-full hover:bg-primary-900/30"
                                                title="Edit"
                                            >
                                                <FaEdit size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(car._id)}
                                                className="text-red-400 hover:text-red-300 hover:scale-110 transition-transform p-1.5 rounded-full hover:bg-red-900/30"
                                                title="Delete"
                                            >
                                                <MdDelete size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!isLoading && cars.length > 0 && (
                <div className="flex justify-center items-center gap-4 mt-6">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 rounded-lg font-medium text-slate-700 bg-white hover:bg-blue-100 border border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Prev
                    </button>
                    <span className="text-slate-600 font-medium">
                        Page <span className="text-white">{currentPage}</span> of <span className="text-white">{Math.ceil(cars.length / itemsPerPage)}</span>
                    </span>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(cars.length / itemsPerPage)))}
                        disabled={currentPage === Math.ceil(cars.length / itemsPerPage)}
                        className="px-4 py-2 rounded-lg font-medium text-slate-700 bg-white hover:bg-blue-100 border border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        Next
                    </button>
                </div>
            )}

            <Modal
                isOpen={modalIsOpen}
                closeModal={closeModal}
                title={isEdit ? "Edit Car" : "Create Car"}
            >
                <div className="flex flex-col w-full max-w-md mx-auto p-2">
                    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                        <div className="grid grid-cols-1 gap-5">
                            {inputFields.map((field, index) => (
                                <div key={index} className="flex flex-col gap-1.5">
                                    <label htmlFor={field.name} className="text-sm font-medium text-slate-700">
                                        {field.label}
                                    </label>
                                    <input
                                        type={field?.type}
                                        name={field?.name}
                                        value={field?.name?.includes("colorDetails")
                                            ? formData.colorDetails[field?.name?.split('.')[1]]
                                            : formData[field?.name]}
                                        onChange={handleChange}
                                        className="input-field"
                                        placeholder={`Enter ${field.label.toLowerCase()}`}
                                        required
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-blue-200/50">
                            <button
                                type="button"
                                className="px-5 py-2.5 rounded-lg font-medium text-slate-700 bg-white hover:bg-blue-100 transition-colors border border-blue-200"
                                onClick={closeModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-5 py-2.5 rounded-lg font-medium text-white shadow-lg transition-transform hover:-translate-y-0.5 active:translate-y-0"
                                style={{ backgroundColor: dealershipTheme }}
                            >
                                {isEdit ? "Update Car" : "Create Car"}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
};

export default Cars;
